"use client";

import { useEffect, useRef } from "react";

const SORO_SRC =
  "https://app.trysoro.com/api/embed/4f43ad0a-137f-42e9-8658-cec99dc79062";

/**
 * Soro blog embed loader.
 *
 * Soro serves a widget script that hydrates the #soro-blog container with the
 * client's published articles. We inject it on mount — mirroring Analytics.tsx's
 * client-side loader pattern rather than next/script, which this build avoids —
 * and remove it on unmount so client-side navigation re-initializes the widget
 * into a fresh container.
 *
 * The component never calls setState, so React never re-renders it after mount
 * and therefore never reconciles (and clears) the DOM that Soro injects into the
 * #soro-blog div. A MutationObserver hides the loading state via the DOM the
 * moment Soro adds content, so we stay out of React's render cycle entirely.
 *
 * Soro serves the embed with Cache-Control: max-age=3600, so a freshly published
 * article can take up to an hour to appear. We bucket a cache-busting param into
 * ~10-minute windows: fresh enough that new posts show quickly, still cached
 * within each window so we do not refetch on every page view.
 */
export default function SoroBlog() {
  const containerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const fallbackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const hideLoader = () => {
      if (loaderRef.current) loaderRef.current.hidden = true;
    };

    // Hide the loader (and any fallback) the moment Soro injects content.
    let observer: MutationObserver | undefined;
    if (container) {
      observer = new MutationObserver(() => {
        if (container.childNodes.length > 0) {
          hideLoader();
          if (fallbackRef.current) fallbackRef.current.hidden = true;
          observer?.disconnect();
        }
      });
      observer.observe(container, { childList: true });
    }

    const script = document.createElement("script");
    script.src = `${SORO_SRC}?v=${Math.floor(Date.now() / 600000)}`;
    script.defer = true;
    script.setAttribute("data-soro-embed", "true");
    document.body.appendChild(script);

    // If the widget has not populated after 8s — e.g. the Soro embed is disabled
    // or the service is unreachable — drop the loader and reveal a graceful
    // fallback so the live page never shows a blank section. The observer above
    // hides the fallback again the instant real content arrives.
    const timeout = window.setTimeout(() => {
      hideLoader();
      if (container && container.childNodes.length === 0 && fallbackRef.current) {
        fallbackRef.current.hidden = false;
      }
    }, 8000);

    return () => {
      observer?.disconnect();
      window.clearTimeout(timeout);
      script.remove();
    };
  }, []);

  return (
    <>
      <div
        ref={loaderRef}
        className="flex items-center justify-center py-16 text-slate-500"
        aria-live="polite"
      >
        <span
          className="h-5 w-5 mr-3 rounded-full border-2 border-slate-200 border-t-[#E8BD4E] animate-spin"
          aria-hidden="true"
        />
        Loading articles…
      </div>

      <div id="soro-blog" ref={containerRef} className="soro-blog-embed" />

      <div ref={fallbackRef} hidden className="text-center py-12">
        <p className="text-lg text-slate-600 max-w-xl mx-auto">
          Fresh chimney tips and articles are on the way. In the meantime, browse
          our guides on inspections, sweeping, repairs, and staying safe all
          season.
        </p>
        <a
          href="/blog"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#E8BD4E] text-[#0A0A0A] font-bold px-8 py-4 rounded-full transition-colors hover:bg-[#d9ad3e] min-h-[48px] text-lg shadow-lg shadow-[#E8BD4E]/30"
        >
          Read Our Chimney Blog
        </a>
      </div>
    </>
  );
}
